import { createServerFn } from "@tanstack/react-start";
import type {
  ListIntegrationAccountsApiWorkspacesWorkspaceIdIntegrationsAccountsGetParams,
  UserIntegrationAccountListResponse,
} from "@/lib/api/generated";
import {
  listIntegrationAccountsApiWorkspacesWorkspaceIdIntegrationsAccountsGet,
  createUnipileConnectionApiWorkspacesWorkspaceIdIntegrationsUnipileConnectPost,
  saveYcloudWhatsappBusinessTokenApiWorkspacesWorkspaceIdIntegrationsYcloudWhatsappBusinessTokenPost,
} from "@/lib/api/generated/user-integrations/user-integrations";
import { getIntegrationAccountCountsApiWorkspacesWorkspaceIdIntegrationsAccountsCountsGet } from "@/lib/api/generated/user-integrations/user-integrations";
import { logError, logInfo } from "@/lib/structured-log";
import type {
  CreateUnipileConnectionRequestIntegrationType,
  CreateUnipileConnectionResponse,
} from "@/lib/api/generated";
import type { UserIntegrationCountsResponse } from "@/lib/api/generated";

const INTEGRATION_ACCOUNTS_TIMEOUT_MS = 120_000;

export const listIntegrationAccountsServer = createServerFn({ method: "GET" })
  .validator(
    (data: {
      workspaceId: string;
      params?: ListIntegrationAccountsApiWorkspacesWorkspaceIdIntegrationsAccountsGetParams;
    }) => data,
  )
  .handler(async ({ data }): Promise<UserIntegrationAccountListResponse> => {
    try {
      logInfo("user_integrations.accounts.list_request", {
        params: data.params,
      });
      const response = await listIntegrationAccountsApiWorkspacesWorkspaceIdIntegrationsAccountsGet(
        data.workspaceId,
        data.params,
        { timeoutMs: INTEGRATION_ACCOUNTS_TIMEOUT_MS } as RequestInit & { timeoutMs: number },
      );

      if (response.status !== 200) {
        logError("user_integrations.accounts.list_failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          page: data.params?.page ?? 1,
          page_size: data.params?.page_size ?? 20,
        });
        throw new Error(`Failed to list integration accounts: ${response.status}`);
      }

      logInfo("user_integrations.accounts.list_success", {
        workspace_id: data.workspaceId,
        total: response.data.total,
        page: response.data.page,
        page_size: response.data.page_size,
        total_pages: response.data.total_pages,
      });
      return response.data;
    } catch (error) {
      logError("user_integrations.accounts.list_error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

export const saveYCloudWhatsappBusinessTokenServer = createServerFn({ method: "POST" })
  .validator((data: { workspaceId: string; token: string; accountIdentifier: string }) => data)
  .handler(async ({ data }): Promise<void> => {
    logInfo("user_integrations.ycloud.save_token.started", {
      workspace_id: data.workspaceId,
    });

    try {
      const response =
        await saveYcloudWhatsappBusinessTokenApiWorkspacesWorkspaceIdIntegrationsYcloudWhatsappBusinessTokenPost(
          data.workspaceId,
          { token: data.token, account_identifier: data.accountIdentifier },
        );

      if (response.status !== 201) {
        logError("user_integrations.ycloud.save_token.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(
          JSON.stringify({ detail: "Failed to save token", status: response.status }),
        );
      }

      logInfo("user_integrations.ycloud.save_token.success", {
        workspace_id: data.workspaceId,
      });
    } catch (error) {
      logError("user_integrations.ycloud.save_token.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });

export const createUnipileConnectionServer = createServerFn({ method: "POST" })
  .validator(
    (data: {
      workspaceId: string;
      integrationType: CreateUnipileConnectionRequestIntegrationType;
    }) => data,
  )
  .handler(async ({ data }): Promise<CreateUnipileConnectionResponse> => {
    logInfo("user_integrations.unipile.create_connection.started", {
      workspace_id: data.workspaceId,
      integration_type: data.integrationType,
    });

    try {
      const response =
        await createUnipileConnectionApiWorkspacesWorkspaceIdIntegrationsUnipileConnectPost(
          data.workspaceId,
          { integration_type: data.integrationType },
        );

      if (response.status !== 200) {
        logError("user_integrations.unipile.create_connection.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
          integration_type: data.integrationType,
        });
        throw new Error(
          JSON.stringify({
            detail: "Failed to create Unipile connection",
            status: response.status,
          }),
        );
      }

      logInfo("user_integrations.unipile.create_connection.success", {
        workspace_id: data.workspaceId,
        integration_type: data.integrationType,
        url: response.data.url,
      });

      return response.data;
    } catch (error) {
      logError("user_integrations.unipile.create_connection.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
        integration_type: data.integrationType,
      });
      throw error;
    }
  });

export const getIntegrationAccountCountsServer = createServerFn({ method: "GET" })
  .validator((data: { workspaceId: string }) => data)
  .handler(async ({ data }): Promise<UserIntegrationCountsResponse> => {
    logInfo("user_integrations.accounts.counts.started", {
      workspace_id: data.workspaceId,
    });
    try {
      const response =
        await getIntegrationAccountCountsApiWorkspacesWorkspaceIdIntegrationsAccountsCountsGet(
          data.workspaceId,
        );
      if (response.status !== 200) {
        logError("user_integrations.accounts.counts.failed", {
          status: response.status,
          workspace_id: data.workspaceId,
        });
        throw new Error(
          JSON.stringify({ detail: "Failed to get integration counts", status: response.status }),
        );
      }
      logInfo("user_integrations.accounts.counts.success", {
        workspace_id: data.workspaceId,
      });
      return response.data;
    } catch (error) {
      logError("user_integrations.accounts.counts.error", {
        error: error instanceof Error ? error.message : "unknown-error",
        workspace_id: data.workspaceId,
      });
      throw error;
    }
  });
